import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, resources, SUPPORTED_LANGUAGES } from './resources';
import { translateLegacyContent } from './legacyContent';

const LanguageContext = createContext(null);

function getStoredLanguage() {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
}

function getTranslation(dictionary, path) {
  return path.split('.').reduce((current, part) => current?.[part], dictionary);
}

function interpolate(template, values = {}) {
  if (typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (_, token) => values[token] ?? `{${token}}`);
}


export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getStoredLanguage);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.body.dataset.language = language;
  }, [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    isEnglish: language === 'en',
    t: (key, values) => {
      const dictionary = resources[language] || resources[DEFAULT_LANGUAGE];
      const fallbackDictionary = resources[DEFAULT_LANGUAGE];
      const translated = getTranslation(dictionary, key) ?? getTranslation(fallbackDictionary, key) ?? key;
      return interpolate(translated, values);
    },
    translateContent: (valueToTranslate) => translateLegacyContent(valueToTranslate, language),
  }), [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
}
